import { getSportsCollectionPage } from '@/api/market';
import { hasSportsContinuation, mergeSportsFirstPage } from '@/domain/sport/sport-feed-refresh.js';
import { paginatedFeedApplyFetchError } from '@/domain/market/feed-fetch-error-state.js';
import {
  createFeedSourceAdapter,
  resolveFeedPaginationMeta,
} from '@/domain/market/feed-source-adapter.js';

export const SPORT_FEED_PAGE_SIZE = 21;
const continuationRequests = new WeakMap();

export { hasSportsContinuation };

export function cancelSportsContinuation(state) {
  continuationRequests.get(state)?.abort();
  continuationRequests.delete(state);
  state.sportsContinuationLoading = false;
}

function paginationError(reason) {
  // Empty message lets the existing feedback layer use markets.loadFailed
  // in the active language; diagnostic details stay out of user copy.
  return Object.assign(new Error(), { code: 'SPORTS_FEED_PAGINATION', reason });
}

export function buildSportFeedQuery(store) {
  const sportsSlug = String(store.sportsSlug ?? '').trim();
  return sportsSlug ? { sportsSlug } : {};
}

/**
 * Sport live 列表分页：getSportsCollectionPage → rawRows（collection 原样，卡片映射走 mapFeedCardsForPresentation）
 * @param {object} ctx paginated-feed-core fetchPageResult 上下文
 */
export async function fetchSportFeedPageResult(ctx) {
  const { state, append, pageNum, pageSize = SPORT_FEED_PAGE_SIZE, rawRowsLength } = ctx;
  const sportsSlug = String(state.sportsSlug ?? '').trim();
  const nextPage = append ? pageNum + 1 : 1;
  const data = await getSportsCollectionPage(sportsSlug ? { sportsSlug } : {}, {
    pageNum: nextPage,
    pageSize,
    offset: append ? state.sportsNextOffset ?? pageNum * pageSize : 0,
    cursor: append ? state.sportsNextCursor : '',
    signal: ctx.signal,
  });
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const modern =
    typeof data?.hasMore === 'boolean' &&
    Number.isSafeInteger(data?.nextOffset) &&
    typeof data?.nextCursor === 'string';
  const { hasMore: legacyHasMore } = resolveFeedPaginationMeta({
    data,
    rows,
    pageSize,
    rawRowsLength: append ? rawRowsLength : 0,
    append,
  });

  const hasMore = modern ? data.hasMore : legacyHasMore;
  const nextOffset = modern ? data.nextOffset : nextPage * pageSize;
  if (
    modern &&
    (nextOffset < 0 || (hasMore && nextOffset <= (append ? state.sportsNextOffset ?? 0 : 0)))
  ) {
    throw paginationError('offset did not advance');
  }
  if (
    modern &&
    hasMore &&
    (!data.nextCursor || (append && data.nextCursor === state.sportsNextCursor))
  ) {
    throw paginationError('cursor did not advance');
  }
  if (ctx.patchExisting) {
    const keepLaterPages = pageNum > 1;
    return {
      apiRows: keepLaterPages ? mergeSportsFirstPage(state.rawRows, rows) : rows,
      nextPage: keepLaterPages ? pageNum : 1,
      hasMore: keepLaterPages ? state.hasMore : hasMore,
      sportsNextOffset: keepLaterPages ? state.sportsNextOffset : nextOffset,
      sportsNextCursor: keepLaterPages ? state.sportsNextCursor : modern ? data.nextCursor : '',
      sportsFirstPageIds: rows.map((row) => String(row.id)),
      authoritativeRows: true,
      preservePagination: keepLaterPages,
      sportsFeedModern: keepLaterPages ? state.sportsFeedModern : modern,
      sportsPendingContinuation: keepLaterPages
        ? state.sportsPendingContinuation
        : modern && hasMore && rows.length === 0,
    };
  }
  const seen = new Set(append ? state.rawRows.map((row) => String(row.id)) : []);
  const apiRows = rows.filter((row) => !seen.has(String(row.id)));
  // An empty terminal keyset response has no nextCursor. Keep the last
  // consumed boundary so future tail probes do not restart at the first page.
  const keepBoundary = modern && append && !rows.length && !data.nextCursor && !hasMore;
  return {
    apiRows,
    nextPage,
    hasMore,
    sportsNextOffset: keepBoundary ? state.sportsNextOffset : nextOffset,
    sportsNextCursor: keepBoundary ? state.sportsNextCursor : modern ? data.nextCursor : '',
    sportsFirstPageIds: append ? state.sportsFirstPageIds : rows.map((row) => String(row.id)),
    sportsFeedModern: modern,
    sportsPendingContinuation: modern && hasMore && apiRows.length === 0,
  };
}

export function createSportFeedFetchOptions() {
  return {
    ...createFeedSourceAdapter({
      logTag: 'sportFeed',
      pageSize: SPORT_FEED_PAGE_SIZE,
      buildQuery: buildSportFeedQuery,
      fetchPageResult: fetchSportFeedPageResult,
    }),
    canLoadEmpty: hasSportsContinuation,
    applyPageResult(state, result) {
      state.sportsNextOffset = result.sportsNextOffset;
      state.sportsNextCursor = result.sportsNextCursor;
      state.sportsFirstPageIds = result.sportsFirstPageIds;
      state.sportsFeedModern = result.sportsFeedModern;
      state.sportsPendingContinuation = result.sportsPendingContinuation;
      state.sportsRetryAt = 0;
    },
  };
}

/** Commit each page separately; cancellation or a later failure keeps progress. */
export async function continueSportsFeed(
  state,
  { signal, probe = false, maxPages = 2, manual = false } = {}
) {
  if (
    signal?.aborted ||
    state.listLoading ||
    state.loadingMore ||
    state.sportsPageLoading ||
    state.sportsContinuationLoading ||
    (!manual && (state.sportsRefreshActive === false || Date.now() < state.sportsRetryAt))
  )
    return false;
  const tail =
    probe &&
    state.sportsFeedModern &&
    !state.hasMore &&
    state.pageNum > 1 &&
    state.sportsNextCursor;
  if (!tail && !hasSportsContinuation(state)) return false;
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  continuationRequests.set(state, controller);
  state.sportsContinuationLoading = true;
  const generation = ++state.loadGeneration;
  const slug = state.sportsSlug;
  const current = () =>
    !controller.signal.aborted && generation === state.loadGeneration && slug === state.sportsSlug;
  const apply = createSportFeedFetchOptions().applyPageResult;
  try {
    for (let page = 0; page < Math.min(maxPages, probe ? 1 : 2); page += 1) {
      const snapshot = { ...state, rawRows: state.rawRows };
      const result = await fetchSportFeedPageResult({
        state: snapshot,
        append: true,
        pageNum: snapshot.pageNum,
        rawRowsLength: snapshot.rawRows.length,
        signal: controller.signal,
      });
      if (
        !current() ||
        snapshot.sportsNextCursor !== state.sportsNextCursor ||
        snapshot.sportsNextOffset !== state.sportsNextOffset
      )
        return false;
      state.rawRows = state.rawRows.concat(result.apiRows);
      state.hasMore = result.hasMore;
      // Empty probes must not inflate page count or PC manual-append counters.
      if (result.apiRows.length || result.sportsNextCursor !== snapshot.sportsNextCursor)
        state.pageNum = result.nextPage;
      apply(state, result);
      state.sportsRetryAt = 0;
      state.listFetchError = null;
      if (!hasSportsContinuation(state)) break;
    }
    return true;
  } catch (error) {
    if (!current()) return false;
    state.sportsRetryAt = Date.now() + 30000;
    paginatedFeedApplyFetchError(state, error);
    return false;
  } finally {
    signal?.removeEventListener('abort', abort);
    if (continuationRequests.get(state) === controller) {
      continuationRequests.delete(state);
      state.sportsContinuationLoading = false;
    }
  }
}
